import { useCallback, useEffect, useRef, useState } from "react"

export type AdaptivePollingOutcome = "changed" | "unchanged"

type AdaptivePollingCycleOutcome = AdaptivePollingOutcome | "error"

export interface AdaptivePollingDelayState {
  lastOutcome: AdaptivePollingCycleOutcome | null
  consecutiveFailures: number
  idleDurationMs: number
}

export interface AdaptivePollingSchedulerState {
  lastOutcome: AdaptivePollingCycleOutcome | null
  consecutiveFailures: number
  lastChangedAtMs: number
}

export interface AdaptivePollingJitterRange {
  min: number
  max: number
}

export interface AdaptivePollingContext {
  signal: AbortSignal
}

export interface RunAdaptivePollingOptions {
  resetActivity?: boolean
}

export interface UseAdaptivePollingOptions {
  enabled: boolean
  scopeKey?: string | number
  poll: (context: AdaptivePollingContext) => Promise<AdaptivePollingOutcome>
  getDelayMs: (state: AdaptivePollingDelayState) => number
  initialDelayMs?: number
  jitterRange?: AdaptivePollingJitterRange
  failureDelaysMs?: readonly number[]
  random?: () => number
}

export interface UseAdaptivePollingResult {
  runNow: (options?: RunAdaptivePollingOptions) => void
  resetActivity: () => void
  isPolling: boolean
  consecutiveFailures: number
}

export const DEFAULT_ADAPTIVE_POLLING_FAILURE_DELAYS_MS = [
  15_000, 30_000, 60_000,
] as const

const DEFAULT_JITTER_RANGE: AdaptivePollingJitterRange = { min: 1, max: 1 }

export const createAdaptivePollingSchedulerState = (
  nowMs: number
): AdaptivePollingSchedulerState => ({
  lastOutcome: null,
  consecutiveFailures: 0,
  lastChangedAtMs: nowMs,
})

export const advanceAdaptivePollingSchedulerState = (
  state: AdaptivePollingSchedulerState,
  outcome: AdaptivePollingCycleOutcome,
  nowMs: number
): AdaptivePollingSchedulerState => ({
  lastOutcome: outcome,
  consecutiveFailures: outcome === "error" ? state.consecutiveFailures + 1 : 0,
  lastChangedAtMs: outcome === "changed" ? nowMs : state.lastChangedAtMs,
})

export const getAdaptivePollingDelayState = (
  state: AdaptivePollingSchedulerState,
  nowMs: number
): AdaptivePollingDelayState => ({
  lastOutcome: state.lastOutcome,
  consecutiveFailures: state.consecutiveFailures,
  idleDurationMs: Math.max(0, nowMs - state.lastChangedAtMs),
})

export const getAdaptivePollingFailureDelayMs = (
  consecutiveFailures: number,
  failureDelaysMs: readonly number[] = DEFAULT_ADAPTIVE_POLLING_FAILURE_DELAYS_MS
): number => {
  if (failureDelaysMs.length === 0) {
    throw new Error("failureDelaysMs must contain at least one delay")
  }

  const index = Math.min(
    Math.max(0, consecutiveFailures - 1),
    failureDelaysMs.length - 1
  )
  return failureDelaysMs[index]
}

export const applyAdaptivePollingJitter = (
  delayMs: number,
  range: AdaptivePollingJitterRange = DEFAULT_JITTER_RANGE,
  random: () => number = Math.random
): number => {
  if (range.min <= 0 || range.max < range.min) {
    throw new Error("jitterRange must satisfy 0 < min <= max")
  }

  const normalizedRandom = Math.min(1, Math.max(0, random()))
  const multiplier = range.min + (range.max - range.min) * normalizedRandom
  return Math.max(0, Math.round(delayMs * multiplier))
}

export const useAdaptivePolling = ({
  enabled,
  scopeKey = "default",
  poll,
  getDelayMs,
  initialDelayMs = 0,
  jitterRange = DEFAULT_JITTER_RANGE,
  failureDelaysMs = DEFAULT_ADAPTIVE_POLLING_FAILURE_DELAYS_MS,
  random = Math.random,
}: UseAdaptivePollingOptions): UseAdaptivePollingResult => {
  const pollRef = useRef(poll)
  const getDelayMsRef = useRef(getDelayMs)
  const jitterRangeRef = useRef(jitterRange)
  const failureDelaysMsRef = useRef(failureDelaysMs)
  const randomRef = useRef(random)
  const runNowRef = useRef<(options?: RunAdaptivePollingOptions) => void>(
    () => {}
  )
  const resetActivityRef = useRef<() => void>(() => {})
  const [isPolling, setIsPolling] = useState(false)
  const [consecutiveFailures, setConsecutiveFailures] = useState(0)

  pollRef.current = poll
  getDelayMsRef.current = getDelayMs
  jitterRangeRef.current = jitterRange
  failureDelaysMsRef.current = failureDelaysMs
  randomRef.current = random

  useEffect(() => {
    let disposed = false
    let inFlight = false
    let rerunRequested = false
    let timeoutId: ReturnType<typeof setTimeout> | null = null
    let controller: AbortController | null = null
    let schedulerState = createAdaptivePollingSchedulerState(Date.now())
    let activityRevision = 0

    const isPageAvailable = () =>
      (typeof document === "undefined" ||
        document.visibilityState === "visible") &&
      (typeof navigator === "undefined" || navigator.onLine !== false)

    const clearScheduledPoll = () => {
      if (timeoutId !== null) {
        clearTimeout(timeoutId)
        timeoutId = null
      }
    }

    const schedulePoll = (delayMs: number) => {
      clearScheduledPoll()
      if (disposed || !enabled || !isPageAvailable()) return

      timeoutId = setTimeout(() => {
        timeoutId = null
        void runPoll()
      }, delayMs)
    }

    const scheduleNextPoll = () => {
      const delayState = getAdaptivePollingDelayState(
        schedulerState,
        Date.now()
      )
      const nominalDelayMs =
        schedulerState.consecutiveFailures > 0
          ? getAdaptivePollingFailureDelayMs(
              schedulerState.consecutiveFailures,
              failureDelaysMsRef.current
            )
          : getDelayMsRef.current(delayState)

      schedulePoll(
        applyAdaptivePollingJitter(
          nominalDelayMs,
          jitterRangeRef.current,
          randomRef.current
        )
      )
    }

    const requestImmediatePoll = (options?: RunAdaptivePollingOptions) => {
      if (disposed || !enabled) return

      if (options?.resetActivity) {
        activityRevision += 1
        schedulerState = createAdaptivePollingSchedulerState(Date.now())
        setConsecutiveFailures(0)
      }

      if (!isPageAvailable()) return

      if (inFlight) {
        rerunRequested = true
        return
      }

      schedulePoll(0)
    }

    async function runPoll() {
      if (disposed || !enabled || !isPageAvailable()) return
      if (inFlight) {
        rerunRequested = true
        return
      }

      inFlight = true
      setIsPolling(true)
      controller = new AbortController()
      const activeController = controller
      const cycleActivityRevision = activityRevision
      let outcome: AdaptivePollingCycleOutcome

      try {
        outcome = await pollRef.current({ signal: activeController.signal })
      } catch {
        outcome = "error"
      } finally {
        inFlight = false
        controller = null
        if (!disposed) setIsPolling(false)
      }

      if (disposed) return

      if (activeController.signal.aborted) {
        if (rerunRequested && isPageAvailable()) {
          rerunRequested = false
          schedulePoll(0)
        }
        return
      }

      if (cycleActivityRevision === activityRevision) {
        schedulerState = advanceAdaptivePollingSchedulerState(
          schedulerState,
          outcome,
          Date.now()
        )
      }
      setConsecutiveFailures(schedulerState.consecutiveFailures)

      if (rerunRequested) {
        rerunRequested = false
        schedulePoll(0)
        return
      }

      scheduleNextPoll()
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        requestImmediatePoll()
        return
      }

      clearScheduledPoll()
      rerunRequested = false
      controller?.abort()
    }

    const handleFocus = () => requestImmediatePoll()
    const handleOnline = () => requestImmediatePoll()
    const handleOffline = () => {
      clearScheduledPoll()
      rerunRequested = false
      controller?.abort()
    }

    const resetActivity = () => {
      if (disposed || !enabled) return
      activityRevision += 1
      schedulerState = createAdaptivePollingSchedulerState(Date.now())
      setConsecutiveFailures(0)
      if (!inFlight) scheduleNextPoll()
    }

    runNowRef.current = requestImmediatePoll
    resetActivityRef.current = resetActivity
    setIsPolling(false)
    setConsecutiveFailures(0)

    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", handleVisibilityChange)
    }
    if (typeof window !== "undefined") {
      window.addEventListener("focus", handleFocus)
      window.addEventListener("online", handleOnline)
      window.addEventListener("offline", handleOffline)
    }

    if (enabled && isPageAvailable()) {
      schedulePoll(
        applyAdaptivePollingJitter(
          initialDelayMs,
          jitterRangeRef.current,
          randomRef.current
        )
      )
    }

    return () => {
      disposed = true
      runNowRef.current = () => {}
      resetActivityRef.current = () => {}
      clearScheduledPoll()
      rerunRequested = false
      controller?.abort()
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", handleVisibilityChange)
      }
      if (typeof window !== "undefined") {
        window.removeEventListener("focus", handleFocus)
        window.removeEventListener("online", handleOnline)
        window.removeEventListener("offline", handleOffline)
      }
    }
  }, [enabled, initialDelayMs, scopeKey])

  const runNow = useCallback((options?: RunAdaptivePollingOptions) => {
    runNowRef.current(options)
  }, [])

  const resetActivity = useCallback(() => {
    resetActivityRef.current()
  }, [])

  return { runNow, resetActivity, isPolling, consecutiveFailures }
}
